/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState } from 'react';
import {
    Box, Paper, Typography, TextField, Button,
    InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';

const BASE_URL = process.env.REACT_APP_CONNECTOR_API_URL;

function ChangePassword() {
    const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState({ current: false, new: false, confirm: false });
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(err => ({ ...err, [name]: '' }));
        setApiError('');
    };

    const validate = () => {
        const e = {};
        if (!form.currentPassword) e.currentPassword = 'Current password is required.';
        if (!form.newPassword)     e.newPassword     = 'New password is required.';
        else if (form.newPassword.length < 8) e.newPassword = 'Must be at least 8 characters.';
        if (form.newPassword !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        setSubmitting(true);
        setApiError('');

        try {
            const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
            const res = await fetch(`${BASE_URL}/api/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    currentPassword: form.currentPassword,
                    newPassword: form.newPassword,
                }),
            });

            const data = await res.json().catch(() => ({}));

            if (res.status === 401) {
                setApiError('Session expired. Please log in again.');
                return;
            }
            if (!res.ok) {
                setApiError(data.error || 'Failed to change password. Please try again.');
                return;
            }

            setSuccess(true);
            setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch {
            setApiError('Unable to reach the server. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const passwordField = (label, name, showKey) => (
        <TextField
            fullWidth
            label={label}
            name={name}
            type={show[showKey] ? 'text' : 'password'}
            value={form[name]}
            onChange={handleChange}
            error={Boolean(errors[name])}
            helperText={errors[name]}
            margin="normal"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))} edge="end">
                            {show[showKey] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: 6, px: 2 }}>
            <Paper
                elevation={0}
                sx={{ width: '100%', maxWidth: 480, borderRadius: 3, border: '1px solid #e0e7ef', p: 4 }}
            >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    Change Password
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter your current password and choose a new one.
                </Typography>

                {success && (
                    <Alert
                        severity="success"
                        icon={<CheckCircleOutline fontSize="inherit" />}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        Password changed successfully!
                    </Alert>
                )}

                {apiError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setApiError('')}>
                        {apiError}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    {passwordField('Current Password', 'currentPassword', 'current')}
                    {passwordField('New Password', 'newPassword', 'new')}
                    {passwordField('Confirm New Password', 'confirmPassword', 'confirm')}

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2 }}
                    >
                        {submitting ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}

export default ChangePassword;
